import { useMemo, useState } from "react";
import AppLayout from "@/app/AppLayout";
import { LGoButton } from "@/ui/branded";
import { AsyncBoundary, PageHeader } from "@/ui/patterns";
import { useToast } from "@/ui/primitives/use-toast";
import { useActivities } from "@/features/activities/hooks/useActivities";
import { useUserBusiness } from "@/features/settings/hooks/useUserBusiness";
import { NoBusinessYet } from "@/features/settings/components/NoBusinessYet";
import ActivityList from "@/features/activities/components/ActivityList";
import ActivityFormDialog, {
  type ActivityFormData,
} from "@/features/activities/components/ActivityFormDialog";
import DeleteActivityDialog from "@/features/activities/components/DeleteActivityDialog";
import {
  toActivityCreateInput,
  toActivityUpdateInput,
} from "@/features/activities/activity-input";
import { useOffers } from "@/features/offers/hooks/useOffers";
import { OfferFormDialog } from "@/features/offers/components/OfferFormDialog";
import type { OfferDraft } from "@/features/offers/hooks/useOfferForm";
import { salesRefusalMessage } from "@/features/offers/sales-locks";
import type { OfferActivityOption } from "@/data/repos/partnerOffersRepo";
import { isApiError } from "@/data/api/client";
import { Plus, Loader2 } from "lucide-react";
import type { Activity } from "@/domain/activity/types";

function toOfferActivityOption(activity: Activity): OfferActivityOption {
  return {
    id: activity.id,
    name: activity.name,
    priceOriginal: activity.priceOriginal,
    customFields: activity.customFields,
    defaultPriceDiscounted: activity.defaultPriceDiscounted,
    defaultCapacity: activity.defaultCapacity,
    saleLeadTimeHours: activity.saleLeadTimeHours,
    saleEndOffsetMinutes: activity.saleEndOffsetMinutes,
    saleEndAnchor: activity.saleEndAnchor,
    defaultDurationMinutes: activity.defaultDurationMinutes,
  };
}

function BusinessLoading() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
    </div>
  );
}

export default function ActivitiesPage() {
  const { toast } = useToast();
  const {
    hasBusiness,
    isLoading: isLoadingBusiness,
    error: businessError,
    refetch: refetchBusiness,
  } = useUserBusiness();
  const {
    activities,
    subcategories,
    isLoading,
    createActivity,
    updateActivity,
    deleteActivity,
  } = useActivities();

  const { activities: offerActivityOptions, createOffer } = useOffers();

  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [publishedActivity, setPublishedActivity] = useState<Activity | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The offers-side option carries the checkout questions this portal may not be
  // able to render, which the activity read here has already dropped; the local
  // one only stands in until that list arrives.
  const publishedActivityOption = useMemo<OfferActivityOption | null>(() => {
    if (!publishedActivity) return null;
    return (
      offerActivityOptions.find((option) => option.id === publishedActivity.id) ??
      toOfferActivityOption(publishedActivity)
    );
  }, [offerActivityOptions, publishedActivity]);

  const handleCreate = () => {
    setSelectedActivity(null);
    setFormOpen(true);
  };

  const handleEdit = (activity: Activity) => {
    setSelectedActivity(activity);
    setFormOpen(true);
  };

  const handleDelete = (activity: Activity) => {
    setSelectedActivity(activity);
    setDeleteOpen(true);
  };

  const handlePublishOffer = (activity: Activity) => {
    setPublishedActivity(activity);
  };

  const handleOfferSubmit = async (draft: OfferDraft) => {
    if (!publishedActivity) return;
    try {
      await createOffer.mutateAsync(draft);
      toast({
        title: "Termín vypsán",
        description: `Slevový termín na „${publishedActivity.name}“ je teď v přehledu Slevové termíny.`,
      });
      setPublishedActivity(null);
    } catch (error) {
      console.error("[publish-offer]", error);
      toast({
        variant: "destructive",
        title: "Nepodařilo se vypsat termín.",
        description: salesRefusalMessage(error) ?? "Zkuste to prosím znovu.",
      });
    }
  };

  const handleFormSubmit = async (data: ActivityFormData) => {
    setIsSubmitting(true);
    try {
      if (selectedActivity) {
        await updateActivity.mutateAsync({
          id: selectedActivity.id,
          ...toActivityUpdateInput(data),
        });
        toast({
          title: "Šablona slev upravena",
          description: `"${data.activity_name}" byla úspěšně upravena.`,
        });
      } else {
        await createActivity.mutateAsync(toActivityCreateInput(data));
        toast({
          title: "Šablona slev vytvořena",
          description: `"${data.activity_name}" byla úspěšně vytvořena.`,
        });
      }
      setFormOpen(false);
    } catch (error) {
      console.error("[activity-form]", error);
      const message =
        error instanceof Error
          ? error.message
          : typeof error === "object" && error && "message" in error
            ? String((error as { message: unknown }).message)
            : "";
      toast({
        variant: "destructive",
        title: selectedActivity
          ? "Nepodařilo se upravit šablonu slev."
          : "Nepodařilo se vytvořit šablonu slev.",
        description: message || "Zkuste to prosím znovu.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedActivity) return;
    setIsSubmitting(true);
    try {
      await deleteActivity.mutateAsync(selectedActivity.id);
      toast({
        title: "Šablona slev smazána",
        description: `"${selectedActivity.name}" byla úspěšně smazána.`,
      });
      setDeleteOpen(false);
    } catch (error) {
      if (isApiError(error, "ACTIVITY_HAS_RESERVATIONS")) {
        toast({
          variant: "destructive",
          title: "Šablonu slev nelze smazat",
          description: `"${selectedActivity.name}" zůstává, protože k jejím termínům patří rezervace nebo nedokončené platby.`,
        });
        setDeleteOpen(false);
        return;
      }
      toast({
        variant: "destructive",
        title: "Chyba",
        description: "Nepodařilo se smazat šablonu slev.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppLayout>
      <AsyncBoundary<boolean>
        data={hasBusiness}
        isLoading={isLoadingBusiness}
        error={businessError}
        onRetry={refetchBusiness}
        loadingFallback={<BusinessLoading />}
      >
        {(partnerHasBusiness) =>
          partnerHasBusiness ? (
            <div className="space-y-8">
              <PageHeader
                title="Šablony slev"
                actions={
                  <LGoButton onClick={handleCreate} size="lg" iconLeft={<Plus />}>
                    Nová šablona slev
                  </LGoButton>
                }
              />

              <ActivityList
                activities={activities}
                isLoading={isLoading}
                onPublishOffer={handlePublishOffer}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />

              <ActivityFormDialog
                open={formOpen}
                onOpenChange={setFormOpen}
                activity={selectedActivity}
                subcategories={subcategories}
                onSubmit={handleFormSubmit}
                isSubmitting={isSubmitting}
              />

              <OfferFormDialog
                open={publishedActivityOption !== null}
                onOpenChange={(open) => !open && setPublishedActivity(null)}
                activities={publishedActivityOption ? [publishedActivityOption] : []}
                sales={{ sold: 0 }}
                reservedSeats={0}
                onSubmit={handleOfferSubmit}
                isLoading={createOffer.isPending}
              />

              <DeleteActivityDialog
                open={deleteOpen}
                onOpenChange={setDeleteOpen}
                activityName={selectedActivity?.name ?? ""}
                onConfirm={handleDeleteConfirm}
                isDeleting={isSubmitting}
              />
            </div>
          ) : (
            <NoBusinessYet
              eyebrow="Šablony slev"
              headline="Nejprve podnik, pak šablony."
              body="Šablony slev (jóga, masáž, kvíz…) patří k podniku. Pojďme ho založit — pak je sem můžete přidávat libovolně."
            />
          )
        }
      </AsyncBoundary>
    </AppLayout>
  );
}
